/**
 * Scene — owns the camera and composes the whole world each frame:
 * sidewalks, lanes, manholes/coins, barriers, chicken, traffic, particles.
 * Also the glue that turns Game bus events into animations.
 */
import { VIEW_W, VIEW_H, SIDEWALK_W, LANE_W, MANHOLE_Y, laneCenterX, worldWidth } from './Layout.js';
import { formatMultiplier } from '../game/Difficulty.js';

export default class Scene {
  constructor({ canvas, sprites, game, bus, chicken, traffic }) {
    this.canvas = canvas;
    // willReadFrequently keeps the canvas on the CPU raster path — sprite
    // blitting stays fast, and hidden/backgrounded tabs still composite frames.
    this.ctx2d = canvas.getContext('2d', { willReadFrequently: true });
    this.sprites = sprites;
    this.game = game;
    this.bus = bus;
    this.chicken = chicken;
    this.traffic = traffic;

    this.camX = 0;
    this.shake = 0;
    this.time = 0;
    this.deathFlash = 0;
    /** @type {Map<number, {t:number}>} lane → pass animation (barrier + coin) */
    this.passed = new Map();
    this.particles = [];

    this._fit = { scale: 1, ox: 0, oy: 0 };
    window.addEventListener('resize', () => this._resize());
    this._resize();
    this._wire();
    this._syncTraffic();
  }

  _resize() {
    const dpr = Math.min(2, window.devicePixelRatio || 1);
    const w = window.innerWidth, h = window.innerHeight;
    this.canvas.width = w * dpr;
    this.canvas.height = h * dpr;
    this.canvas.style.width = `${w}px`;
    this.canvas.style.height = `${h}px`;
    const scale = Math.min(w / VIEW_W, h / VIEW_H);
    this._fit = { dpr, scale, ox: (w - VIEW_W * scale) / 2, oy: (h - VIEW_H * scale) / 2 };
  }

  _wire() {
    this.bus.on('chicken:hop', ({ from, to, deadly, duration }) => {
      this.chicken.hopTo(laneCenterX(to), duration, () => this.game.landed());
      if (deadly) {
        this.traffic.setContext(this._trafficCtx({ blockedLane: to }));
        this.traffic.dispatchKiller(to, duration + 90);
        this.bus.emit('sfx:horn', {});
      } else {
        this.traffic.rushLane(to, Math.max(80, duration - 130));
        this.traffic.setContext(this._trafficCtx({ blockedLane: to }));
      }
      // the manhole we leave behind turns into a coin behind a barrier
      if (from >= 1) this.passed.set(from, { t: 0 });
    });

    this.bus.on('chicken:landed', () => this._syncTraffic());

    this.bus.on('round:dead', () => {
      this.chicken.die();
      this.shake = 1;
      this.deathFlash = 1;
      this._burstFeathers();
      this._syncTraffic();
    });

    this.bus.on('round:cashout', () => {
      this.chicken.celebrate();
      this._burstSparkles();
      this._syncTraffic();
    });

    this.bus.on('round:reset', () => {
      this.chicken.reset();
      this.passed.clear();
      this._syncTraffic();
    });

    this.bus.on('game:difficulty', () => {
      this.passed.clear();
      this.traffic.clear();
      this._syncTraffic();
    });
  }

  _trafficCtx(over = {}) {
    const g = this.game;
    const idle = g.state === 'idle' || g.state === 'dead' || g.state === 'cashed';
    return {
      minLane: idle ? 1 : Math.min(g.lane + 2, g.difficulty.lanes),
      maxLane: g.difficulty.lanes,
      blockedLane: null,
      ...over,
    };
  }

  _syncTraffic() {
    this.traffic.setContext(this._trafficCtx());
  }

  /* ---------------- particles ---------------- */

  _burstFeathers() {
    const x = this.chicken.worldX, y = MANHOLE_Y - 10;
    for (let i = 0; i < 17; i++) {
      const a = Math.random() * Math.PI * 2;
      const v = 90 + Math.random() * 240;
      this.particles.push({
        kind: 'feather', x, y: y - 20,
        vx: Math.cos(a) * v, vy: Math.sin(a) * v - 140,
        rot: Math.random() * 6, vrot: (Math.random() - 0.5) * 9,
        age: 0, life: 0.9 + Math.random() * 0.5, scale: 0.7 + Math.random() * 0.7,
      });
    }
  }

  _burstSparkles() {
    const x = this.chicken.worldX, y = MANHOLE_Y - 70;
    for (let i = 0; i < 14; i++) {
      const a = (i / 14) * Math.PI * 2 + Math.random() * 0.4;
      const v = 60 + Math.random() * 150;
      this.particles.push({
        kind: 'spark', x, y,
        vx: Math.cos(a) * v, vy: Math.sin(a) * v - 60,
        rot: 0, vrot: 0, age: 0, life: 0.7 + Math.random() * 0.4, scale: 0.6 + Math.random() * 0.8,
      });
    }
  }

  landDust(x) {
    for (let i = 0; i < 6; i++) {
      this.particles.push({
        kind: 'dust', x: x + (Math.random() - 0.5) * 50, y: MANHOLE_Y + 38 + Math.random() * 8,
        vx: (Math.random() - 0.5) * 70, vy: -20 - Math.random() * 30,
        rot: 0, vrot: 0, age: 0, life: 0.35, scale: 0.6 + Math.random() * 0.7,
      });
    }
  }

  /* ---------------- frame ---------------- */

  update(dt) {
    this.time += dt;
    const wasAir = this.chicken.airT > 0.3;
    this.chicken.update(dt);
    if (wasAir && this.chicken.airT === 0 && this.chicken.mode === 'idle') {
      this.landDust(this.chicken.worldX);
    }

    // camera follows, keeping the chicken in the left third
    const lanes = this.game.difficulty.lanes;
    const target = Math.max(0, Math.min(this.chicken.worldX - 330, worldWidth(lanes) - VIEW_W));
    this.camX += (target - this.camX) * (1 - Math.exp(-5.5 * dt));

    this.traffic.update(dt, this.camX);

    if (this.shake > 0) this.shake *= Math.exp(-4.2 * dt);
    if (this.deathFlash > 0) this.deathFlash = Math.max(0, this.deathFlash - dt * 1.6);

    for (const lane of this.passed.keys()) {
      const p = this.passed.get(lane);
      p.t = Math.min(1.2, p.t + dt * 2.6);
    }

    for (const p of this.particles) {
      p.age += dt;
      p.vy += (p.kind === 'feather' ? 420 : p.kind === 'spark' ? 260 : 60) * dt;
      const drag = p.kind === 'feather' ? Math.exp(-2.2 * dt) : 1;
      p.vx *= drag;
      p.vy *= p.kind === 'feather' ? Math.exp(-1.4 * dt) : 1;
      p.x += p.vx * dt;
      p.y += p.vy * dt;
      p.rot += p.vrot * dt;
    }
    this.particles = this.particles.filter((p) => p.age < p.life);
  }

  draw() {
    const { dpr, scale, ox, oy } = this._fit;
    const ctx = this.ctx2d;
    const s = this.sprites;
    const g = this.game;
    const lanes = g.difficulty.lanes;

    ctx.setTransform(1, 0, 0, 1, 0, 0);
    ctx.fillStyle = '#191b20';
    ctx.fillRect(0, 0, this.canvas.width, this.canvas.height);

    const shx = (Math.random() - 0.5) * 16 * this.shake;
    const shy = (Math.random() - 0.5) * 12 * this.shake;
    ctx.setTransform(dpr * scale, 0, 0, dpr * scale, dpr * (ox + shx * scale), dpr * (oy + shy * scale));

    // clip to the logical viewport so world art never bleeds into letterbox
    ctx.save();
    ctx.beginPath();
    ctx.rect(0, 0, VIEW_W, VIEW_H);
    ctx.clip();

    const cam = this.camX;

    // ground
    const swl = s.sidewalkLeft;
    if (-cam + SIDEWALK_W > 0) ctx.drawImage(swl, -cam, 0, swl.logicalW, swl.logicalH);
    for (let k = 1; k <= lanes; k++) {
      const x = SIDEWALK_W + (k - 1) * LANE_W - cam;
      if (x > VIEW_W || x + LANE_W < 0) continue;
      ctx.drawImage(s.laneStrip, x, 0, LANE_W, VIEW_H);
    }
    const rightX = SIDEWALK_W + lanes * LANE_W - cam;
    if (rightX < VIEW_W) ctx.drawImage(s.sidewalkRight, rightX, 0, SIDEWALK_W, VIEW_H);

    // sidewalk dressing
    this._blitAt(s.tree, 84 - cam, 108);
    this._blitAt(s.lamp, 148 - cam, 150);
    this._blitAt(s.tree, 66 - cam, 620);
    this._blitAt(s.tree, rightX + 120, 96);
    this._blitAt(s.tree, rightX + 136, 600);

    // manholes / coins
    for (let k = 1; k <= lanes; k++) {
      const x = laneCenterX(k) - cam;
      if (x < -80 || x > VIEW_W + 80) continue;
      const pass = this.passed.get(k);
      if (pass) {
        // coin flip-in
        const t = Math.min(1, pass.t);
        const flip = t < 0.5 ? Math.abs(Math.cos(t * Math.PI * 2)) : 1;
        const pop = 1 + 0.25 * Math.sin(Math.min(1, t) * Math.PI);
        ctx.save();
        ctx.translate(x, MANHOLE_Y);
        ctx.scale(flip * pop, pop);
        ctx.drawImage(s.coin, -s.coin.logicalW / 2, -s.coin.logicalH / 2, s.coin.logicalW, s.coin.logicalH);
        ctx.restore();
      } else {
        ctx.drawImage(s.manhole, x - s.manhole.logicalW / 2, MANHOLE_Y - s.manhole.logicalH / 2, s.manhole.logicalW, s.manhole.logicalH);
        this._manholeText(ctx, x, MANHOLE_Y, g.ladder[k]);
      }
    }

    // barriers drop onto passed lanes, slightly above the manhole row
    for (const [lane, pass] of this.passed) {
      const x = laneCenterX(lane) - cam;
      if (x < -100 || x > VIEW_W + 100) continue;
      const t = Math.min(1, pass.t);
      const drop = 1 - (1 - t) * (1 - t); // ease-out fall
      const bounce = t > 0.8 ? Math.sin((t - 0.8) * 5 * Math.PI) * 4 * (1 - t) : 0;
      const y = MANHOLE_Y - 150 - (1 - drop) * 120 + bounce;
      ctx.globalAlpha = Math.min(1, t * 3);
      ctx.drawImage(s.barrier, x - s.barrier.logicalW / 2, y - s.barrier.logicalH / 2, s.barrier.logicalW, s.barrier.logicalH);
      ctx.globalAlpha = 1;
    }

    // hero + traffic (cars draw above the chicken — they run it over)
    this.chicken.draw(ctx, cam);
    this.traffic.draw(ctx, cam);

    // particles
    for (const p of this.particles) {
      const fade = 1 - p.age / p.life;
      const x = p.x - cam;
      if (p.kind === 'feather') {
        ctx.save();
        ctx.globalAlpha = Math.min(1, fade * 1.6);
        ctx.translate(x, p.y);
        ctx.rotate(p.rot);
        ctx.scale(p.scale, p.scale);
        ctx.drawImage(s.feather, -12, -12, 24, 24);
        ctx.restore();
      } else if (p.kind === 'spark') {
        ctx.save();
        ctx.globalAlpha = fade;
        ctx.translate(x, p.y);
        ctx.fillStyle = '#ffd75e';
        const r = 7 * p.scale;
        ctx.beginPath();
        ctx.moveTo(0, -r);
        ctx.quadraticCurveTo(2, -2, r, 0);
        ctx.quadraticCurveTo(2, 2, 0, r);
        ctx.quadraticCurveTo(-2, 2, -r, 0);
        ctx.quadraticCurveTo(-2, -2, 0, -r);
        ctx.fill();
        ctx.restore();
      } else {
        ctx.globalAlpha = fade * 0.5;
        ctx.fillStyle = '#c9cdd4';
        ctx.beginPath();
        ctx.arc(x, p.y, 8 * p.scale + p.age * 26, 0, Math.PI * 2);
        ctx.fill();
        ctx.globalAlpha = 1;
      }
    }

    // red flash on death
    if (this.deathFlash > 0) {
      ctx.fillStyle = `rgba(200,30,20,${0.22 * this.deathFlash})`;
      ctx.fillRect(0, 0, VIEW_W, VIEW_H);
    }

    ctx.restore();
  }

  _blitAt(spr, cx, cy) {
    if (cx < -160 || cx > VIEW_W + 160) return;
    this.ctx2d.drawImage(spr, cx - spr.logicalW / 2, cy - spr.logicalH / 2, spr.logicalW, spr.logicalH);
  }

  _manholeText(ctx, x, y, mult) {
    if (mult == null) return;
    const label = `${formatMultiplier(mult)}x`;
    const size = label.length <= 5 ? 24 : label.length <= 7 ? 19 : 15;
    ctx.font = `800 ${size}px 'Arial Black', Arial, sans-serif`;
    ctx.textAlign = 'center';
    ctx.textBaseline = 'middle';
    // engraved: dark below, light face
    ctx.fillStyle = 'rgba(24,26,31,0.9)';
    ctx.fillText(label, x, y + 1.5);
    ctx.fillStyle = '#d3d7de';
    ctx.fillText(label, x, y);
  }
}
